/**
 * FilterPanel — Sidebar section [4]: `list` slot.
 *
 * Drives the filters applied to the AuthorListView. Emits `filter:change`
 * on the bus whenever any control is touched. Includes a text search with
 * autocomplete. (The LIM virtual keyboard was removed: on the whiteboard the
 * filters are enough to find authors/lessons.)
 */

import { BaseSection, type SectionId } from '@/core/BaseSection';
import type { PhilosopherFilters } from '@/services/IDataService';
import type { AnnoScolastico, PhilosopherLocalized, ProblemaFondamentale } from '@/data/schema';
import { PROBLEMI_FONDAMENTALI } from '@/data/schema';
import { problemaLabel } from '@/views/format';
import { getDataService } from '@/services/data-registry';

export class FilterPanel extends BaseSection {
  readonly sectionId: SectionId = 'list';
  readonly sectionTitle = 'Filtri filosofi';

  private annoSelect!: HTMLSelectElement;
  private problemaSelect!: HTMLSelectElement;
  private resetBtn!: HTMLButtonElement;
  private searchInput!: HTMLInputElement;
  private suggestionsEl!: HTMLElement;
  private allAuthors: PhilosopherLocalized[] = [];

  protected async onInit(): Promise<void> {
    this.element.classList.add('filter-panel');

    this.element.innerHTML = `
      <div class="filter-panel-inner">
        <h3 class="filter-title">Filtri</h3>

        <div class="filter-row filter-search-row">
          <span class="filter-label">Cerca autore</span>
          <div class="filter-search-wrapper">
            <input type="text" class="filter-input" data-role="search" placeholder="Nome filosofo…" autocomplete="off" />
            <div class="filter-suggestions" data-role="suggestions" hidden></div>
          </div>
        </div>

        <label class="filter-row">
          <span class="filter-label">Anno scolastico</span>
          <select class="filter-select" data-role="anno">
            <option value="">Tutti</option>
            <option value="3">Terza</option>
            <option value="4">Quarta</option>
            <option value="5">Quinta</option>
          </select>
        </label>

        <label class="filter-row">
          <span class="filter-label">Problema fondamentale</span>
          <select class="filter-select" data-role="problema">
            <option value="">Tutti</option>
            ${PROBLEMI_FONDAMENTALI.map(
              (p) => `<option value="${p}">${problemaLabel(p)}</option>`,
            ).join('')}
          </select>
        </label>

        <div class="filter-actions">
          <button type="button" class="btn btn-secondary" data-role="reset">Reset filtri</button>
        </div>
      </div>
    `;

    this.annoSelect = this.element.querySelector<HTMLSelectElement>('[data-role="anno"]')!;
    this.problemaSelect = this.element.querySelector<HTMLSelectElement>('[data-role="problema"]')!;
    this.resetBtn = this.element.querySelector<HTMLButtonElement>('[data-role="reset"]')!;
    this.searchInput = this.element.querySelector<HTMLInputElement>('[data-role="search"]')!;
    this.suggestionsEl = this.element.querySelector<HTMLElement>('[data-role="suggestions"]')!;

    // Load all authors for autocomplete
    const data = getDataService();
    this.allAuthors = await data.listAuthors({}, this.context.locale);

    this.addEventListener(this.annoSelect, 'change', () => this.emitChange());
    this.addEventListener(this.problemaSelect, 'change', () => this.emitChange());
    this.addEventListener(this.resetBtn, 'click', () => {
      this.annoSelect.value = '';
      this.problemaSelect.value = '';
      this.searchInput.value = '';
      this.hideSuggestions();
      this.emitChange();
      this.context.bus.emit('navigate:home', {});
    });

    // Search autocomplete
    this.addEventListener(this.searchInput, 'input', () => this.onSearchInput());
    this.addEventListener(this.searchInput, 'focus', () => this.onSearchInput());
    this.addEventListener(this.suggestionsEl, 'click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-author-id]');
      if (btn?.dataset.authorId) {
        this.searchInput.value = '';
        this.hideSuggestions();
        this.context.bus.emit('navigate:author', { id: btn.dataset.authorId });
      }
    });

    // Close suggestions on outside click
    this.addEventListener(document, 'click', (ev) => {
      if (!this.element.contains(ev.target as Node)) {
        this.hideSuggestions();
      }
    });
  }

  private onSearchInput(): void {
    const query = this.searchInput.value.trim().toLowerCase();
    if (query.length === 0) {
      this.hideSuggestions();
      return;
    }

    const matches = this.allAuthors
      .filter((a) => a.nome.toLowerCase().includes(query))
      .slice(0, 3);

    if (matches.length === 0) {
      this.suggestionsEl.innerHTML = `<div class="filter-suggestion filter-no-match">Nessun risultato</div>`;
      this.suggestionsEl.hidden = false;
      return;
    }

    this.suggestionsEl.innerHTML = matches
      .map(
        (a) =>
          `<button type="button" class="filter-suggestion" data-author-id="${esc(a.id)}">${highlight(a.nome, query)}</button>`,
      )
      .join('');
    this.suggestionsEl.hidden = false;
  }

  private hideSuggestions(): void {
    this.suggestionsEl.hidden = true;
    this.suggestionsEl.innerHTML = '';
  }

  private emitChange(): void {
    const filters: PhilosopherFilters = {};
    if (this.annoSelect.value) {
      filters.anno_scolastico = Number(this.annoSelect.value) as AnnoScolastico;
    }
    if (this.problemaSelect.value) {
      filters.problema_fondamentale = this.problemaSelect.value as ProblemaFondamentale;
    }
    this.context.bus.emit<PhilosopherFilters>('filter:change', filters);
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function highlight(name: string, query: string): string {
  const idx = name.toLowerCase().indexOf(query);
  if (idx === -1) return esc(name);
  const before = name.slice(0, idx);
  const match = name.slice(idx, idx + query.length);
  const after = name.slice(idx + query.length);
  return `${esc(before)}<strong>${esc(match)}</strong>${esc(after)}`;
}
